import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';

const ABAS = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

function dataISO(valor) {
  if (valor === null || valor === undefined || valor === '') return null;

  if (typeof valor === 'number') {
    const partes = XLSX.SSF.parse_date_code(valor);
    if (!partes) return null;

    const ano = String(partes.y).padStart(4, '0');
    const mes = String(partes.m).padStart(2, '0');
    const dia = String(partes.d).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  if (valor instanceof Date && !Number.isNaN(valor.getTime())) {
    return valor.toISOString().slice(0, 10);
  }

  const texto = String(valor).trim();
  const brasileira = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (brasileira) {
    return `${brasileira[3]}-${brasileira[2].padStart(2, '0')}-${brasileira[1].padStart(2, '0')}`;
  }

  const data = new Date(texto);
  return Number.isNaN(data.getTime()) ? null : data.toISOString().slice(0, 10);
}

function texto(valor) {
  const resultado = String(valor ?? '').trim();
  return resultado || null;
}

function valorNumerico(valor) {
  if (typeof valor === 'number' && Number.isFinite(valor)) return valor;

  const convertido = Number(
    String(valor ?? '')
      .replace(/\s/g, '')
      .replace(/R\$/gi, '')
      .replace(/\./g, '')
      .replace(',', '.')
  );

  return Number.isFinite(convertido) ? convertido : 0;
}

function extrairLinhas(workbook) {
  const linhas = [];

  for (const aba of ABAS) {
    const planilha = workbook.Sheets[aba];
    if (!planilha) continue;

    const dados = XLSX.utils.sheet_to_json(planilha, {
      header: 1,
      raw: true,
      defval: null,
    });

    for (let indice = 9; indice < dados.length; indice += 1) {
      const colunas = dados[indice] || [];
      const cliente = texto(colunas[2]);

      if (!cliente) continue;

      linhas.push({
        aba,
        linha: indice + 1,
        numero: texto(colunas[0]),
        data_solicitacao: dataISO(colunas[1]),
        cliente,
        vendedor_externo: texto(colunas[3]),
        referencia_comercial: texto(colunas[4]),
        valor: valorNumerico(colunas[5]),
        data_envio: dataISO(colunas[6]),
        status_original: texto(colunas[7]),
        etapa_original: texto(colunas[8]),
        responsavel_atual: texto(colunas[9]),
        data_aprovacao: dataISO(colunas[10]),
        data_pedido: dataISO(colunas[11]),
        data_entrega: dataISO(colunas[12]),
        ultimo_contato: dataISO(colunas[14]),
        proximo_followup: dataISO(colunas[15]),
        observacoes_historicas: texto(colunas[16]),
      });
    }
  }

  return linhas;
}

async function importarApi(linhas, confirmar) {
  const token = localStorage.getItem('flowtech_token');

  const resposta = await fetch('/api/crm/importar-historico', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ linhas, confirmar }),
  });

  const dados = await resposta.json().catch(() => null);

  if (!resposta.ok) {
    throw new Error(
      dados?.error ||
      dados?.mensagem ||
      `Erro HTTP ${resposta.status}`
    );
  }

  return dados;
}

export default function CrmImportadorHistorico({
  currentUser,
  onImportado,
}) {
  const inputRef = useRef(null);
  const [aberto, setAberto] = useState(false);
  const [arquivo, setArquivo] = useState('');
  const [linhas, setLinhas] = useState([]);
  const [previa, setPrevia] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [processando, setProcessando] = useState(false);

  if (currentUser?.perfil !== 'admin') return null;

  function limpar() {
    setArquivo('');
    setLinhas([]);
    setPrevia(null);
    setResultado(null);
    setErro('');
    setProcessando(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  function fechar() {
    setAberto(false);
    limpar();
  }

  async function selecionarArquivo(evento) {
    const selecionado = evento.target.files?.[0];
    limpar();

    if (!selecionado) return;

    if (!/\.xlsx?$/i.test(selecionado.name)) {
      setErro('Selecione uma planilha Excel no formato .xlsx ou .xls.');
      return;
    }

    setProcessando(true);

    try {
      const buffer = await selecionado.arrayBuffer();
      const workbook = XLSX.read(buffer, {
        type: 'array',
        cellDates: false,
      });
      const registros = extrairLinhas(workbook);

      if (!registros.length) {
        throw new Error(
          'Nenhum registro foi encontrado nas abas mensais da planilha.'
        );
      }

      setArquivo(selecionado.name);
      setLinhas(registros);

      const analise = await importarApi(registros, false);
      setPrevia(analise);
    } catch (falha) {
      setErro(falha.message || 'Não foi possível analisar a planilha.');
    } finally {
      setProcessando(false);
    }
  }

  async function confirmarImportacao() {
    if (!linhas.length || !previa) return;

    setProcessando(true);
    setErro('');

    try {
      const importacao = await importarApi(linhas, true);
      setResultado(importacao);
      if (onImportado) await onImportado();
    } catch (falha) {
      setErro(falha.message || 'Não foi possível importar o histórico.');
    } finally {
      setProcessando(false);
    }
  }

  return (
    <>
      <button
        className="btn btn-ghost"
        onClick={() => setAberto(true)}
      >
        Importar histórico
      </button>

      {aberto && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{ maxWidth: 720, width: '94%' }}
          >
            <div
              className="modal-head"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div>
                <div className="modal-title">
                  Importar histórico comercial
                </div>
                <div
                  style={{
                    color: 'var(--muted)',
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  A planilha será processada sem permanecer armazenada.
                </div>
              </div>

              <button
                className="modal-close"
                onClick={fechar}
                style={{
                  border: 0,
                  background: 'transparent',
                  fontSize: 24,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {!resultado && (
                <>
                  <label className="field">
                    <span>Planilha comercial (.xlsx)</span>
                    <input
                      ref={inputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={selecionarArquivo}
                      disabled={processando}
                    />
                  </label>

                  {arquivo && (
                    <div
                      style={{
                        marginTop: 12,
                        fontSize: 13,
                        color: 'var(--muted)',
                      }}
                    >
                      Arquivo analisado: <strong>{arquivo}</strong>
                    </div>
                  )}
                </>
              )}

              {processando && (
                <div
                  className="card"
                  style={{ padding: 18, marginTop: 16 }}
                >
                  Processando histórico comercial...
                </div>
              )}

              {erro && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 14,
                    borderRadius: 8,
                    color: 'var(--danger)',
                    background: 'rgba(239,68,68,.08)',
                    border: '1px solid rgba(239,68,68,.25)',
                  }}
                >
                  {erro}
                </div>
              )}

              {previa && !resultado && (
                <div
                  className="card"
                  style={{ padding: 18, marginTop: 16 }}
                >
                  <div className="card-title">
                    Prévia da importação
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: 12,
                      marginTop: 14,
                    }}
                  >
                    <div>
                      <div className="kpi-label">Registros</div>
                      <div className="kpi-value" style={{ fontSize: 22 }}>
                        {previa.oportunidades_validas}
                      </div>
                    </div>
                    <div>
                      <div className="kpi-label">Empresas</div>
                      <div className="kpi-value" style={{ fontSize: 22 }}>
                        {previa.empresas_identificadas}
                      </div>
                    </div>
                    <div>
                      <div className="kpi-label">Ignorados</div>
                      <div className="kpi-value" style={{ fontSize: 22 }}>
                        {previa.linhas_ignoradas}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 14,
                      fontSize: 12,
                      color: 'var(--muted)',
                    }}
                  >
                    A confirmação criará ou atualizará empresas e
                    oportunidades, sem duplicar linhas já importadas.
                  </div>
                </div>
              )}

              {resultado && (
                <div
                  className="card"
                  style={{ padding: 18 }}
                >
                  <div
                    className="card-title"
                    style={{ color: 'var(--success)' }}
                  >
                    Histórico importado com sucesso
                  </div>

                  <div style={{ marginTop: 14, lineHeight: 1.8 }}>
                    <div>
                      Oportunidades criadas:{' '}
                      <strong>{resultado.oportunidades_criadas}</strong>
                    </div>
                    <div>
                      Oportunidades atualizadas:{' '}
                      <strong>{resultado.oportunidades_atualizadas}</strong>
                    </div>
                    <div>
                      Empresas criadas:{' '}
                      <strong>{resultado.empresas_criadas}</strong>
                    </div>
                    <div>
                      Empresas atualizadas:{' '}
                      <strong>{resultado.empresas_atualizadas}</strong>
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 12,
                      fontSize: 12,
                      color: 'var(--muted)',
                    }}
                  >
                    O arquivo original não foi armazenado no servidor.
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={fechar}
                disabled={processando}
              >
                {resultado ? 'Fechar' : 'Cancelar'}
              </button>

              {previa && !resultado && (
                <button
                  className="btn btn-primary"
                  onClick={confirmarImportacao}
                  disabled={processando}
                >
                  Confirmar importação
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
